import React, { useState, useEffect } from 'react';
import { SQUARE_CONFIG } from '../config/squareConfig';
import { processSquarePayment } from '../api/squareApi';
import { getCreditCardsByPassengerId } from '../api/getCreditCards';

const SquarePayment = ({ 
  bookingRefId, 
  amount, 
  passengerId,
  paymentType = 'FULL', // 'FULL' or 'PARTIAL'
  onSuccess, 
  onError,
  onLoading 
}) => {
  const [card, setCard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('new'); // 'new' or 'saved'
  const [errors, setErrors] = useState({});

  const isInitializingRef = React.useRef(false);
  const cardInstanceRef = React.useRef(null);

  useEffect(() => {
    if (!isInitializingRef.current) {
      isInitializingRef.current = true;
      initializeSquare();
    }
    if (passengerId) {
      loadSavedCards();
    }

    return () => {
      if (cardInstanceRef.current) {
        cardInstanceRef.current.destroy().catch(() => {});
        cardInstanceRef.current = null;
      }
      isInitializingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerId]);

  useEffect(() => {
    if (onLoading) {
      onLoading(isLoading);
    }
  }, [isLoading, onLoading]);

  const initializeSquare = async () => {
    try {
      if (!window.Square) {
        throw new Error('Square Web Payments SDK not loaded');
      }

      const container = document.getElementById('card-container');
      if (!container) return;

      // If card already attached or container already has children, don't duplicate
      if (cardInstanceRef.current) {
        try {
          await cardInstanceRef.current.destroy();
        } catch (e) {}
        cardInstanceRef.current = null;
      }
      container.innerHTML = '';

      const payments = window.Square.payments(
        SQUARE_CONFIG.applicationId, 
        SQUARE_CONFIG.locationId
      );
      
      const cardStyle = {
        '.input-container': {
          borderColor: '#334155',
          borderRadius: '10px'
        },
        '.input-container.is-focus': {
          borderColor: '#3b82f6'
        },
        '.input-container.is-error': {
          borderColor: '#ef4444'
        },
        'input': {
          backgroundColor: '#0f172a',
          color: '#ffffff',
          fontFamily: 'sans-serif'
        },
        'input::placeholder': {
          color: '#94a3b8'
        }
      };

      const cardInstance = await payments.card({
        style: cardStyle
      });
      await cardInstance.attach('#card-container');
      cardInstanceRef.current = cardInstance;
      setCard(cardInstance);
    } catch (error) {
      console.error('Square initialization failed:', error);
      setErrors({ init: 'Payment form initialization failed. Please refresh the page.' });
    }
  };

  const loadSavedCards = async () => {
    try {
      const cards = await getCreditCardsByPassengerId(passengerId);
      setSavedCards(cards?.data || cards || []);
    } catch (error) {
      console.error('[SquarePayment] Error loading saved cards:', error);
      setSavedCards([]);
    }
  };

  const handleSquareErrors = (error) => {
    const errorMessages = {
      'VALIDATION_ERROR': 'Please check your card details',
      'CARD_DECLINED': 'Your card was declined. Please try another card',
      'INSUFFICIENT_FUNDS': 'Insufficient funds. Please try another card',
      'GENERIC_DECLINE': 'Payment was declined. Please contact your bank',
      'INVALID_CARD': 'Invalid card number. Please check and try again',
      'EXPIRED_CARD': 'Card has expired. Please use a different card',
      'INCORRECT_CVV': 'Incorrect CVV code. Please check and try again',
      'INVALID_EXPIRATION': 'Invalid expiration date. Please check and try again',
      'INVALID_POSTAL_CODE': 'Invalid postal code. Please check and try again'
    };

    if (error.category && errorMessages[error.category]) {
      return errorMessages[error.category];
    }
    if (error.code && errorMessages[error.code]) {
      return errorMessages[error.code];
    }
    return error.message || 'Payment processing failed. Please try again.';
  };

  const handlePayment = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      if (paymentMethod === 'saved' && selectedCardId) {
        const paymentData = {
          booking_ref_id: bookingRefId,
          amount: parseFloat(paymentAmount),
          passenger_id: passengerId,
          payment_type: paymentType,
          payment_method: 'SAVED_CARD',
          card_id: selectedCardId,
          currency: 'USD'
        };

        const result = await processSquarePayment(paymentData);
        if (result.status === 'COMPLETED' || result.status === 'APPROVED' || result.success) {
          if (onSuccess) onSuccess(result);
        } else {
          throw new Error(result.message || 'Payment processing failed');
        }
      } else {
        if (!card) {
          throw new Error('Payment form is not ready. Please refresh.');
        }

        const tokenResult = await card.tokenize();
        if (tokenResult.status === 'OK') {
          const paymentData = {
            booking_ref_id: bookingRefId,
            amount: parseFloat(paymentAmount),
            passenger_id: passengerId,
            payment_type: paymentType,
            payment_method: 'NEW_CARD',
            source_id: tokenResult.token,
            currency: 'USD'
          };

          const result = await processSquarePayment(paymentData);
          if (result.status === 'COMPLETED' || result.status === 'APPROVED' || result.success) {
            if (onSuccess) onSuccess(result);
          } else {
            throw new Error(result.message || 'Payment processing failed');
          }
        } else {
          const errorMessage = handleSquareErrors(tokenResult.errors[0]);
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      console.error('[SquarePayment] Payment process failed:', error);
      setErrors({ payment: error.message });
      if (onError) onError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const paymentAmount = paymentType === 'PARTIAL' ? '1.00' : amount;

  return (
    <div className="square-payment-form w-full p-6 sm:p-8 bg-slate-950/80 rounded-2xl border border-slate-800 shadow-xl">
      <div className="mb-6 pb-4 border-b border-slate-800">
        <h3 className="text-xl font-bold text-white mb-2 tracking-tight">Credit Card Authorization</h3>
        <div className="bg-slate-900/90 p-4 rounded-xl border border-slate-800 flex justify-between items-center">
          <span className="text-xs uppercase font-bold text-slate-400">Total Authorized Charge:</span>
          <span className="text-2xl font-black text-white font-mono">${paymentAmount}</span>
        </div>
        {paymentType === 'PARTIAL' && (
          <span className="text-xs text-slate-400 block mt-2 font-medium">
            (Authorization hold of $1.00 - final balance charged upon trip completion)
          </span>
        )}
      </div>

      {errors.init && (
        <div className="mb-4 p-3.5 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-300 text-xs">
          {errors.init}
        </div>
      )}

      {savedCards.length > 0 && (
        <div className="mb-6">
          <h4 className="text-xs uppercase font-bold text-slate-400 mb-3 tracking-wider">Payment Method</h4>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200">
              <input
                type="radio"
                name="paymentMethod"
                value="new"
                checked={paymentMethod === 'new'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span>Enter New Card</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-200">
              <input
                type="radio"
                name="paymentMethod"
                value="saved"
                checked={paymentMethod === 'saved'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span>Saved Corporate Card</span>
            </label>
          </div>
        </div>
      )}

      {paymentMethod === 'saved' && savedCards.length > 0 && (
        <div className="mb-6">
          <h4 className="text-xs uppercase font-bold text-slate-400 mb-2">Saved Cards on File</h4>
          <select 
            value={selectedCardId} 
            onChange={(e) => setSelectedCardId(e.target.value)}
            className="w-full p-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm outline-none focus:border-blue-500"
          >
            <option value="">Select a saved card</option>
            {savedCards.map(card => (
              <option key={card.creditcard_id} value={card.creditcard_id}>
                {card.card_number_masked} - {card.card_type}
              </option>
            ))}
          </select>
        </div>
      )}

      {paymentMethod === 'new' && (
        <div className="mb-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Card Information
            </label>
            <div 
              id="card-container" 
              className="min-h-[50px] border border-slate-700 rounded-xl bg-slate-900/90 p-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all"
            ></div>
          </div>
        </div>
      )}

      {errors.payment && (
        <div className="mb-4 p-3.5 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-300 text-xs flex items-center gap-2">
          <span className="material-symbols-outlined text-rose-400 !text-base">error</span>
          <span>{errors.payment}</span>
        </div>
      )}

      <button 
        onClick={handlePayment} 
        disabled={isLoading || (paymentMethod === 'new' && !card)}
        className={`w-full py-4 px-4 rounded-xl font-extrabold uppercase tracking-wider text-xs transition-all shadow-xl flex items-center justify-center gap-2 ${
          isLoading || (paymentMethod === 'new' && !card)
            ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700' 
            : 'exec-btn-primary'
        }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            <span>Encrypting & Authorizing...</span>
          </div>
        ) : (
          <>
            <span className="material-symbols-outlined !text-base">lock</span>
            <span>Authorize & Confirm Reservation (${paymentAmount})</span>
          </>
        )}
      </button>

      <div className="mt-4 text-[11px] text-slate-400 text-center flex items-center justify-center gap-2">
        <span className="material-symbols-outlined text-emerald-400 !text-sm">security</span>
        <span>100% Secure & Encrypted Payment</span>
      </div>
    </div>
  );
};

export default SquarePayment;
